import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, map, Observable, of, switchMap } from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { DataBindingSourceInfo } from '@blaze-shared/elements-domain';
import { syncSelect } from '@blaze-shared/platform-data-access';

import { deriveSourceWorkflowOutcome, SourceWorkflowOutcome } from './source-workflow-outcome';

/**
 * Resolves what a Table's own bound source workflow is doing, and starts it if the workflow was
 * never dispatched into a connection chain by anything else. Table-scoped: each Table instance
 * provides and owns its own kick guard, so it never re-dispatches for a workflow it has  already
 * attempted to start.
 */
@Injectable()
export class TableSourceWorkflowOutcomeService {
  private readonly store = inject(Store);

  private readonly startAttemptedWorkflowIds = new Set<string>();

  observeOutcome$(
    dataSource$: Observable<DataBindingSourceInfo | undefined>,
  ): Observable<SourceWorkflowOutcome> {
    return dataSource$.pipe(
      map((dataSource) => (dataSource?.sourceType === 'context' ? dataSource.id : undefined)),
      distinctUntilChanged(),
      switchMap((workflowId) =>
        workflowId
          ? this.observeWorkflowOutcome$(workflowId)
          : of<SourceWorkflowOutcome>('pending'),
      ),
    );
  }

  private observeWorkflowOutcome$(workflowId: string): Observable<SourceWorkflowOutcome> {
    return combineLatest([
      this.store.select(runtimeSelectors.selectWorkflowsStatusById(workflowId)),
      this.store.select(runtimeSelectors.selectWorkflowOutputsById(workflowId)),
      this.store.select(runtimeSelectors.selectIsRunning),
      this.store.select(runtimeSelectors.selectIsWorkflowQueued(workflowId)),
    ]).pipe(
      map(([status, output, isRunning, isQueued]) =>
        deriveSourceWorkflowOutcome({
          hasStatus: status !== undefined,
          phase: status?.phase,
          hasOutput: output !== undefined,
          isRunning,
          isQueued,
        }),
      ),
      map((outcome) => (outcome === 'unstarted' ? this.startWorkflowOnce(workflowId) : outcome)),
    );
  }

  /** Kicks the workflow at most once per id, then reports whether that kick queued it. */
  private startWorkflowOnce(workflowId: string): SourceWorkflowOutcome {
    if (!this.startAttemptedWorkflowIds.has(workflowId)) {
      this.startAttemptedWorkflowIds.add(workflowId);
      this.store.dispatch(runtimeActions.executeWorkflow({ workflowId }));
    }

    return syncSelect(this.store, runtimeSelectors.selectIsWorkflowQueued(workflowId))
      ? 'pending'
      : 'unstarted';
  }
}
